import React, {forwardRef} from "react";

import LoadingStyles from '@/base/styles/Loading.module.scss'

type Size = 'small' | 'medium'

type Tailwind = string
type Props = {
	children?: React.ReactNode;
	show?: boolean;
	className?: string;
	innerClassName?: Tailwind;
	bodyClassName?: Tailwind;
	onClick?: () => void;
	showOnRoot?: boolean;
	text?: string | null | React.ReactNode;
	size?: Size;
}

const cloudPath = `M52.2,24.5c0,1.7-0.6,3.4-1.6,4.5c-1.9,2.2-4.6,2.3-6.4,2.3c-0.4,0-3.2,0.1-5.5-1.2c-0.4-0.2-1.4-0.8-2.2-1.5
\tc-0.1-0.1-0.5-0.5-1.1-0.9c0,0-0.3-0.2-0.7-0.5c-1.6-1-2.8-1.6-2.8-1.6c-0.6-0.3-1.7-0.8-3.2-1c-0.8-0.1-1.4-0.2-1.9-0.2h0
\tc-1.3,0-2.5,0.2-3.7,0.6c-2.5,0.8-4.2,2-5.8,3.2c0,0-0.1,0-0.1,0.1c-1.7,1.3-3.2,2.3-5.2,2.7C8.5,31.8,3.6,30,1.6,26
\tc-1.1-2.2-1-4.5-0.5-6.3C1.4,18.8,1.7,18,2,17.5c0.3-0.6,0.5-1,0.6-1.1c2.2-3.4,5.8-4.4,7.6-4.8l0.5-0.1l0.1-0.5
\tc0-0.4,0.1-0.9,0.2-1.4c0-0.1,0.2-1,0.6-1.9C12.5,5.5,14,4.1,14,4.1c1.5-1.4,3-2,3.9-2.4c1.6-0.6,3.4-0.9,5.2-0.9
\tc3.3,0,6.1,1.1,7.9,3c0.9,0.9,2,2.4,2.5,4.8l0.2,1L34.6,9c1.5-0.8,3.3-1.2,5-1c2.1,0.2,3.9,1,5.1,2.5c1.1,1.2,1.6,2.8,1.6,4.6
\tc0,0.7-0.1,1.4-0.3,2.1l-0.2,0.8l0.8,0.1c1.2,0.2,2.6,0.7,3.8,1.8C51.7,21.1,52.3,22.8,52.2,24.5z`;

const cloudPath1 = `M23.7,34.7c-0.6,0-1-0.6-0.9-1.1c0.3-0.6,1.5-2.2,4.2-2.2c2.2,0,3.5,1.5,3.9,1.9c0.1,0.2,0.2,0.4,0.2,0.7
\tc-0.1,0.8-1.2,1-1.6,0.4c-0.6-0.8-1.5-1.2-2.5-1.2h0c-1,0-1.9,0.5-2.5,1.2C24.2,34.6,23.9,34.7,23.7,34.7z`
const cloudPath2 = `M32.9,32.6c-0.3,0-0.6-0.1-0.8-0.4c-2.5-3.2-7.8-3.2-10.3,0c-0.8,1-2.4-0.2-1.6-1.2c2.4-3,7-4,10.5-2.3
\tc1.2,0.5,2.2,1.3,3,2.3c0.2,0.3,0.3,0.7,0.1,1C33.6,32.4,33.2,32.6,32.9,32.6z`;

export const Loading = forwardRef(({
	children = null,
	show = false,
	className = "",
	showOnRoot = false,
	innerClassName = '',
	bodyClassName = '',
	size = "medium",
	text = null,
	onClick = () => null
}: Props, ref: React.Ref<HTMLDivElement>) => {
	return (
		<div className={`relative ${className || ''} ${show && showOnRoot ? `opacity-50 blur-sm` : null} `} ref={ref}>
			<div className={`relative duration-100 ${show ? `opacity-50 select-none !pointer-events-none [&_*]:!pointer-events-none blur-sm` : null} ${innerClassName}`} onClick={onClick}>
				{children}
			</div>
			{show && (
				<div role="status" className={`absolute !pointer-events-auto -translate-x-1/2 -translate-y-1/2 top-2/4 left-1/2 bg-main-bg ${bodyClassName} ${size == "medium" ? 'p-3' : 'p-1'}  rounded-xl shadow-2xl flex flex-col items-center space-y-2`}>
					<svg version="1.1"
					     xmlns="http://www.w3.org/2000/svg"
					     x="0px"
					     y="0px"
					     viewBox="0 0 53 38"
					     style={{
						     enableBackground: 'new 0 0 53 38'
					     } as any}
					     className={`mt-1`}
					     width="53" height="38">
						<path
							className={`fill-transparent stroke-gray-500/20 stroke-[2px]`}
							d={cloudPath}></path>
						<path
							className={`fill-gray-500/20`}
							d={cloudPath1}></path>
						<path
							className={`fill-gray-500/20`}
							d={cloudPath2}></path>
						<circle
							className={`fill-gray-500/20`}
							cx="26.9" cy="36.3" r="1.5"></circle>
						<path
							className={`${LoadingStyles['svg-elem-1']} ${LoadingStyles['st0']}`}
							d={cloudPath1}></path>
						<path
							className={`${LoadingStyles['svg-elem-2']} ${LoadingStyles['st1']}`}
							d={cloudPath}></path>
						<path
							className={`${LoadingStyles['svg-elem-3']} ${LoadingStyles['st0']}`}
							d={cloudPath2}></path>
						<circle
							className={`${LoadingStyles['svg-elem-4']} ${LoadingStyles['st0']}`}
							cx="26.9" cy="36.3" r="1.5"></circle>
					</svg>

					{/*<Image src={'/image/loading.svg'} width={50} height={50} alt={"Loading"} className={`mt-1 mx-1 text-gray-400 fill-primary`}/>*/}
					<span className="sr-only">Loading...</span>

					{text && <div className={`text-gray-500 text-sm font-medium flex flex-col`}>{typeof text == "string" ? <p>{text}</p> : text}</div>}
				</div>
			)}
		</div>
	)
});